import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import './TasksTable.css';
import Button from 'components/shared/button/Button';
import QuotesRequestModal from 'components/admin/work-orders/details/quotes/QuotesRequestModal';
import NewTaskModal from 'components/admin/work-orders/details/tasks/NewTaskModal';
import {
  getQuotes,
  getSelectAllChecked,
  getSelectedTasks,
} from 'selectors/admin/work-orders/details/details';
import { ROLES, TYPES, usersUtil } from 'services/utils/users-util';
import {
  WorkOrderStatusEnum,
  WorkOrderStatusKeys,
} from 'services/utils/work-order-status-enum';
import {
  getCurrentWorkOrder,
  getTasks,
  getTrades,
} from 'selectors/admin/work-orders/work-orders';
import { getChangeOrderTasks } from 'components/admin/work-orders/details/change-order-tasks/store/selectors';
import { QuotesStatusKeys } from 'domain/quotes-status-enum';
import { simpleScrollToTop } from 'services/utils/scroller-util';
import {
  deselectAllTasks,
  goToTask,
  loadTradesRequest,
  selectAllTasks,
  selectTask,
  selectTaskRequest,
} from 'actions/admin/work-orders/details/details';
import { openModal } from 'actions/admin/work-orders/details/new-task';
import { getCurrentUser } from 'selectors/authentication';
import { showQuotesRequestModalRequest } from 'actions/admin/work-orders/details/quotes-request';
import { setShowCreateQuotesModal } from 'actions/admin/work-orders/details/create-quote';
import CreateQuotesModal from 'components/admin/work-orders/details/quotes/CreateQuotesModal';
import TasksSimpleTable from 'components/admin/work-orders/details/tasks/TasksSimpleTable';

class TasksTable extends Component {
  static propTypes = {
    currentUser: PropTypes.shape({}).isRequired,
    currentWorkOrder: PropTypes.shape({}).isRequired,
    quotes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    selectAllChecked: PropTypes.bool.isRequired,
    selectedTasks: PropTypes.shape({}).isRequired,
    tasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    trades: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    onDeselectAllTasks: PropTypes.func.isRequired,
    onGoToTask: PropTypes.func.isRequired,
    onLoadTrades: PropTypes.func.isRequired,
    onOpenNewTaskModal: PropTypes.func.isRequired,
    onSelectAllTasks: PropTypes.func.isRequired,
    onSelectTask: PropTypes.func.isRequired,
    onSelectTaskRequest: PropTypes.func.isRequired,
    onShowCreateQuotesModal: PropTypes.func.isRequired,
    onShowQuotesRequestModalRequest: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    const { currentUser } = this.props;
    this.userIsClient = usersUtil.hasType(currentUser, TYPES.CLIENT);
    this.userIsContractor = usersUtil.hasType(currentUser, TYPES.CONTRACTOR);
    this.userIsAdmin =
      usersUtil.isAdmin(currentUser) ||
      usersUtil.hasType(currentUser, TYPES.GENERAL_CONTRACTOR);
    this.userIsSpackleAdmin =
      usersUtil.hasRole(currentUser, ROLES.SPACKLE_ADMIN) ||
      usersUtil.hasType(currentUser, TYPES.GENERAL_CONTRACTOR);
  }

  handleAddTask = () => {
    const { tasks, trades, onOpenNewTaskModal } = this.props;
    let lastTaskNumber = 0;
    if (tasks.length > 0) {
      lastTaskNumber = tasks[tasks.length - 1].number;
    }
    onOpenNewTaskModal(trades.length === 0, lastTaskNumber);
  };

  handleBidsRequest = () => {
    this.props.onShowQuotesRequestModalRequest([], this.mapTrades());
  };

  handleCreateQuotes = () => {
    this.props.onShowCreateQuotesModal(true);
  };

  handleSelectAllCheck = () => {
    this.props.onSelectAllTasks();
  };

  handleTaskCheckSelection = (taskId) => {
    this.props.onSelectTask(taskId);
  };

  handleTaskSelection = (index) => {
    simpleScrollToTop();

    const {
      currentUser,
      currentWorkOrder,
      tasks,
      trades,
      onDeselectAllTasks,
      onGoToTask,
      onLoadTrades,
      onSelectTaskRequest,
    } = this.props;
    const taskId = tasks[index].id;

    if (trades.length === 0) {
      onLoadTrades();
    }
    onSelectTaskRequest(currentWorkOrder.id, taskId, currentUser).then(
      (response) => {
        if (response.ok) {
          onDeselectAllTasks();
          onGoToTask(this.props.currentWorkOrder.id, taskId);
        }
      }
    );
  };

  render() {
    return (
      <div className="tasks-table">
        <TasksSimpleTable
          currentUser={this.props.currentUser}
          currentWorkOrder={this.props.currentWorkOrder}
          quotes={this.props.quotes}
          selectAllChecked={this.props.selectAllChecked}
          selectedTasks={this.props.selectedTasks}
          tasks={this.props.tasks}
          trades={this.props.trades}
          changeOrderTasks={this.props.changeOrderTasks}
          onDeselectAllTasks={this.props.onDeselectAllTasks}
          onGoToTask={this.props.onGoToTask}
          onLoadTrades={this.props.onLoadTrades}
          onSelectAllTasks={this.props.onSelectAllTasks}
          onSelectTask={this.props.onSelectTask}
          onSelectTaskRequest={this.props.onSelectTaskRequest}
        />

        <div className="buttons-container">
          {this.showAddTask() && (
            <Button
              className="button-orange"
              label="Add Task"
              onClick={this.handleAddTask}
            />
          )}
          {this.showRequestBid() && (
            <React.Fragment>
              <Button
                className="button-gray"
                label="Request Bids"
                disabled={Object.keys(this.props.selectedTasks).length === 0}
                onClick={this.handleBidsRequest}
              />

              <QuotesRequestModal />
            </React.Fragment>
          )}
        </div>
        {this.showCreateQuotes() && (
          <div className="buttons-container">
            <Button
              className="button-orange"
              disabled={Object.keys(this.props.selectedTasks).length === 0}
              label="Create Quotes"
              onClick={this.handleCreateQuotes}
            />
          </div>
        )}

        <NewTaskModal />
        <CreateQuotesModal />
      </div>
    );
  }

  mapTrades() {
    const { tasks, selectedTasks } = this.props;
    return tasks
      .map((task) =>
        selectedTasks[task.id] !== undefined ? task.trade.id : null
      )
      .filter((obj) => obj);
  }

  showAddTask() {
    const statusEnum = WorkOrderStatusEnum.create(
      this.props.currentWorkOrder.status
    );
    return (
      ((WorkOrderStatusEnum.PENDING.equals(statusEnum) ||
        WorkOrderStatusEnum.PLM.equals(statusEnum) ||
        WorkOrderStatusEnum.IN_PROGRESS.equals(statusEnum)) &&
        this.userIsSpackleAdmin) ||
      (WorkOrderStatusEnum.PENDING.equals(statusEnum) && this.userIsClient)
    );
  }

  showRequestBid() {
    const statusEnum = WorkOrderStatusEnum.create(
      this.props.currentWorkOrder.status
    );
    return (
      (WorkOrderStatusEnum.PENDING.equals(statusEnum) ||
        WorkOrderStatusEnum.PLM.equals(statusEnum)) &&
      this.userIsSpackleAdmin
    );
  }

  showCreateQuotes() {
    const { currentWorkOrder, quotes } = this.props;
    const correctWOStatus =
      currentWorkOrder.status === WorkOrderStatusKeys.PENDING ||
      currentWorkOrder.status === WorkOrderStatusKeys.PLM ||
      currentWorkOrder.status === WorkOrderStatusKeys.WOA;

    const correctQuotesStatus =
      quotes.filter(
        (quote) =>
          quote.status === QuotesStatusKeys.WAITING ||
          quote.status === QuotesStatusKeys.WITHDRAWN
      ).length > 0;

    return this.userIsContractor && correctWOStatus && correctQuotesStatus;
  }
}

function mapStateToProps(state) {
  return {
    currentUser: getCurrentUser(state),
    currentWorkOrder: getCurrentWorkOrder(state),
    quotes: getQuotes(state),
    selectAllChecked: getSelectAllChecked(state),
    selectedTasks: getSelectedTasks(state),
    tasks: getTasks(state),
    trades: getTrades(state),
    changeOrderTasks: getChangeOrderTasks(state),
  };
}

const mapDispatchToProps = (dispatch) => ({
  onDeselectAllTasks: () => {
    dispatch(deselectAllTasks());
  },
  onGoToTask: (workOrderId, taskId) => {
    dispatch(goToTask(workOrderId, taskId));
  },
  onLoadTrades: () => {
    dispatch(loadTradesRequest());
  },
  onOpenNewTaskModal: (loadTrades, lastTaskNumber) => {
    dispatch(openModal(loadTrades, lastTaskNumber));
  },
  onSelectAllTasks: () => {
    dispatch(selectAllTasks());
  },
  onSelectTask: (taskId) => {
    dispatch(selectTask(taskId));
  },
  onSelectTaskRequest: (workOrderId, taskId, currentUser) => {
    return dispatch(selectTaskRequest(workOrderId, taskId, currentUser));
  },
  onShowCreateQuotesModal: (value) => {
    dispatch(setShowCreateQuotesModal(value));
  },
  onShowQuotesRequestModalRequest: (subContractors, trades) => {
    dispatch(showQuotesRequestModalRequest(subContractors, trades));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(TasksTable);



// WEBPACK FOOTER //
// ./src/components/admin/work-orders/details/tasks/TasksTable.js